// src/components/Footer.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Github,
  ExternalLink,
  Shield,
  MessageCircle,
  Twitter
} from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const navigation = {
    product: [
      { name: 'Create Token', href: '/create', internal: true },
      { name: 'Advanced Tools', href: '/tools', internal: true },
      { name: 'My Bundles', href: '/bundles', internal: true },
      { name: 'Dashboard', href: '/dashboard', internal: true },
    ],
    resources: [
      { name: 'Documentation', href: 'https://docs.alladinlaunch.org', internal: false },
      { name: 'API Reference', href: 'https://api.alladinlaunch.org', internal: false },
      { name: 'Tutorials', href: '#', internal: false },
      { name: 'Blog', href: '#', internal: false },
    ],
    company: [
      { name: 'About', href: '#about', internal: true },
      { name: 'Tokenomics', href: '#tokenomics', internal: true },
      { name: 'Roadmap', href: '#', internal: false },
      { name: 'Contact', href: 'mailto:support@alladinlaunch.org', internal: false },
    ],
    legal: [
      { name: 'Terms of Service', href: '#', internal: false },
      { name: 'Privacy Policy', href: '#', internal: false },
      { name: 'Risk Disclosure', href: '#', internal: false },
    ],
  };

  return (
    <footer className="bg-gray-900/50 backdrop-blur-sm border-t border-gray-800 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                <img
                  src="/lamp.png"
                  alt="Aladdin Lamp Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <span 
  className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" 
  style={{ fontFamily: 'Cinzel, serif' }}
>
  Aladdin
</span>
            </Link>
            <p className="text-gray-400 mb-6 max-w-md">
              Solana's premier token launchpad with advanced bundling, sniping tools, 
              and transparent RevShare economics. Your wish is our command.
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://x.com/aladdinlaunch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors group"
                aria-label="X (Twitter)"
              >
                <Twitter className="w-5 h-5 text-gray-400 group-hover:text-white" />
              </a>

              <a
                href="https://t.me/aladdinlaunchpad"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors group"
                aria-label="Telegram"
              >
                <MessageCircle className="w-5 h-5 text-gray-400 group-hover:text-white" />
              </a>

              <a
                href="https://discord.gg/aladdinlaunch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors group"
                aria-label="Discord"
              >
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-white fill-current"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                </svg>
              </a>

              <a
                href="https://github.com/aladdinlaunch"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors group"
                aria-label="GitHub"
              >
                <Github className="w-5 h-5 text-gray-400 group-hover:text-white" />
              </a>
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Product</h3>
            <ul className="space-y-3">
              {navigation.product.map((item) => (
                <li key={item.name}>
                  {item.internal ? (
                    <Link
                      to={item.href}
                      className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-purple-400 transition-colors text-sm inline-flex items-center"
                    >
                      {item.name}
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Resources</h3>
            <ul className="space-y-3">
              {navigation.resources.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-purple-400 transition-colors text-sm inline-flex items-center"
                  >
                    {item.name}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Aladdin</h3>
            <ul className="space-y-3">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  {item.internal ? (
                    <a
                      href={item.href}
                      className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        

        {/* Bottom Section */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} Aladdin Launchpad. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex space-x-6">
              {navigation.legal.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-400 hover:text-purple-400 transition-colors text-sm"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-4 bg-yellow-900/10 border border-yellow-900/30 rounded-lg">
            <p className="text-xs text-yellow-400/70 text-center">
              <Shield className="w-3 h-3 inline mr-1" />
              Disclaimer: Cryptocurrency investments carry high risk. Token creation and trading on Aladdin Launchpad
              does not guarantee profits. Always do your own research and invest responsibly.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
